import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import LocationSearch from '@/components/LocationSearch';
import { 
  Stethoscope, CheckCircle, ArrowRight, Phone, 
  Heart, ShieldCheck, Users, Sparkles
} from 'lucide-react';

const services = [
  { icon: Stethoscope, title: 'Clinics & Hospitals', description: 'Modern healthcare facilities' },
  { icon: Heart, title: 'Dental Clinics', description: 'Comfortable dental spaces' },
  { icon: Users, title: 'Wellness Centers', description: 'Holistic healing environments' },
  { icon: Sparkles, title: 'Diagnostic Labs', description: 'Efficient diagnostic spaces' },
];

const benefits = [
  'Reduce patient anxiety through design',
  'Improve operational efficiency',
  'Ensure compliance with healthcare standards',
  'Create healing environments',
  'Optimize patient flow',
  'Enhance staff productivity'
];

const HealthcareInterior = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    facilityName: '',
    facilityType: '',
    contactPerson: '',
    phone: '',
    email: '',
    location: '',
    area: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.facilityName || !formData.phone || !formData.location) {
      toast.error('Please fill in all required fields including location');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/leads/interior-healthcare', {
        facility_name: formData.facilityName,
        facility_type: formData.facilityType || null,
        contact_person: formData.contactPerson || null,
        phone: formData.phone,
        email: formData.email || null,
        location: formData.location,
        area_sqft: formData.area ? parseInt(formData.area) : null,
        message: formData.message || null
      });
      
      toast.success(`Request submitted! Your Request ID: ${response.data.request_id}`);
      
      const whatsappMsg = `Hi ZenStudios! I need healthcare interior design. Request ID: ${response.data.request_id}. Facility: ${formData.facilityName}`;
      window.open(`https://wa.me/918790460259?text=${encodeURIComponent(whatsappMsg)}`, '_blank');
      
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="healthcare-interior-page">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-primary/5 via-white to-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Stethoscope className="w-4 h-4" /> Healthcare Design
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Design Healing Spaces That Care
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Create healthcare environments that promote healing, reduce anxiety, and 
                improve patient outcomes. From clinics to hospitals, we design with care.
              </p>
              <div className="flex gap-4 flex-wrap">
                <Button 
                  size="lg"
                  className="rounded-full bg-primary hover:bg-primary/90 px-8"
                  onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Free Consultation <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                  onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%20need%20healthcare%20interior%20design.', '_blank')}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Talk to Expert
                </Button>
              </div>
            </div>
            <div className="relative">
              <img loading="lazy" decoding="async" 
                src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=600" 
                alt="Modern Healthcare Facility"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-10 h-10 text-primary" />
                  <div>
                    <p className="font-semibold">Compliance Ready</p>
                    <p className="text-xs text-gray-500">Healthcare Standards</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Healthcare Facilities We Design</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Specialized design solutions for every type of healthcare facility
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, idx) => (
              <div key={idx} className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <service.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{service.title}</h3>
                <p className="text-sm text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="grid grid-cols-2 gap-4">
              <img loading="lazy" decoding="async" 
                src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=300" 
                alt="Clinic Interior"
                className="rounded-xl"
              />
              <img loading="lazy" decoding="async" 
                src="https://images.unsplash.com/photo-1586773860418-d37222d8fce3?w=300" 
                alt="Waiting Area"
                className="rounded-xl mt-8"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6">Evidence-Based Healthcare Design</h2>
              <p className="text-gray-600 mb-8">
                Research shows that well-designed healthcare spaces can reduce patient stress 
                by 25% and decrease staff turnover. We design with science and empathy.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20" id="contact-form">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Design Your Healthcare Facility</h2>
            <p className="text-gray-600">
              Share your facility requirements and our specialized team will assist you
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-2xl shadow-lg border">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Facility Name *</Label>
                  <Input
                    value={formData.facilityName}
                    onChange={(e) => setFormData({...formData, facilityName: e.target.value})}
                    placeholder="Clinic / Hospital name"
                    required
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>Facility Type</Label>
                  <Input
                    value={formData.facilityType}
                    onChange={(e) => setFormData({...formData, facilityType: e.target.value})}
                    placeholder="Clinic, Hospital, Lab..."
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                    required
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="facility@email.com"
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Location *</Label>
                  <LocationSearch
                    value={formData.location}
                    onChange={(val) => setFormData({...formData, location: val})}
                    placeholder="Search city / area..."
                    types="city,town,locality"
                    required
                    className="rounded-xl mt-1"
                    data-testid="healthcare-interior-location"
                  />
                </div>
                <div>
                  <Label>Approximate Area (sq.ft)</Label>
                  <Input
                    type="number"
                    value={formData.area}
                    onChange={(e) => setFormData({...formData, area: e.target.value})}
                    placeholder="e.g., 2000"
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Project Requirements</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Describe your requirements, specializations, compliance needs..."
                  rows={4}
                  className="rounded-xl mt-1"
                />
              </div>

              <Button type="submit" className="w-full rounded-full py-6 text-lg bg-primary hover:bg-primary/90" disabled={loading}>
                {loading ? 'Submitting...' : 'Request Free Consultation'}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthcareInterior;
